/**
 * MoltsPay Client Types
 */

// Client configuration (stored in ~/.moltspay/)
export interface ClientConfig {
  chain: string;
  limits: {
    maxPerTx: number;
    maxPerDay: number;
  };
  /**
   * Ordered rail preference (2.0.0). When the server's 402 offers more than
   * one rail (e.g. USDC on Base AND CNY via Alipay) and the caller didn't
   * pass an explicit `rail`, the first entry here that the server also
   * accepts wins. e.g. `["base", "alipay"]`.
   */
  railPreference?: string[];
  /**
   * Buyer identity for the custodial balance rail (2.2.0). When set and the
   * server's 402 offers `balance`, the client can pay password-free by
   * carrying this id in the X-Payment payload. Treat it like a bearer token.
   */
  buyerId?: string;
}

// Wallet data (stored in ~/.moltspay/wallet.json)
export interface WalletData {
  address: string;
  privateKey: string;
  createdAt: number;
}

// Payment response from server
export interface PaymentRequired {
  message: string;
  payment: {
    chargeId: string;
    service: string;
    amount: number;
    currency: string;
    wallet: string;
    chain: string;
    expiresAt: number;
  };
}

// Service info from server
export interface ServiceInfo {
  id: string;
  name: string;
  description?: string;
  price: number;
  currency: string;
  input: Record<string, any>;
  output: Record<string, any>;
  available: boolean;
  provider?: ProviderInfo;  // For marketplace listings
  endpoint?: string;  // Custom endpoint path (e.g., for Cloudflare Workers)
}

// Provider info from server
export interface ProviderInfo {
  name: string;
  username?: string;
  description?: string;
  wallet: string;
  chain?: string;
  chains?: string[] | { chain: string }[];  // Multi-chain support
}

// Services response from server
export interface ServicesResponse {
  provider?: ProviderInfo;  // Optional for marketplace listings
  services: ServiceInfo[];
}

// Verify response from server
export interface VerifyResponse {
  status: string;
  chargeId: string;
  txHash: string;
  result: Record<string, any>;
}

// Client options
export interface MoltsPayClientOptions {
  configDir?: string;  // Default: ~/.moltspay
  /** Ordered rail preference; see {@link ClientConfig.railPreference}. */
  railPreference?: string[];
  /** Stable Alipay session id (else AIPAY_SESSION_ID, else a fresh UUID). */
  alipaySessionId?: string;
}
